import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  RefreshCw, ChevronDown, ChevronUp, ExternalLink, Mail, GripVertical,
} from 'lucide-react';
import { auth } from '../firebase';
import { apiHeaders } from '../utils/api';

const API = 'https://aglomera.com/api';

const SLUG_LABEL: Record<string, string> = {
  'corporate-finance': 'Corporate Finance',
  'financial-markets': 'Market Finance',
  'rh':                'Ressources Humaines',
};

const SLUG_ORDER = [
  'corporate-finance',
  'financial-markets',
  'rh',
];

const SLUG_COLOR: Record<string, string> = {
  'corporate-finance': '#1a3a70',
  'financial-markets': '#0f4c81',
  'rh':                '#1a6b9a',
};

const SOURCE_BADGE: Record<string, { label: string; bg: string; color: string }> = {
  wttj:          { label: 'WTTJ',           bg: '#fef9c3', color: '#854d0e' },
  apec_stage:    { label: 'APEC',           bg: '#ede9fe', color: '#5b21b6' },
  apec_emploi:   { label: 'APEC',           bg: '#ede9fe', color: '#5b21b6' },
  hellowork:     { label: 'HelloWork',      bg: '#fce7f3', color: '#9d174d' },
  jobteaser:     { label: 'JobTeaser',      bg: '#dbeafe', color: '#1e40af' },
  linkedin:      { label: 'LinkedIn',       bg: '#dbeafe', color: '#1d4ed8' },
  adzuna:        { label: 'Adzuna',         bg: '#f0fdf4', color: '#166534' },
  france_travail:{ label: 'France Travail', bg: '#fff7ed', color: '#c2410c' },
  meteojob:      { label: 'Météojob',       bg: '#f0f9ff', color: '#0369a1' },
  cadremploi:    { label: 'Cadremploi',     bg: '#fdf4ff', color: '#7e22ce' },
  jobijoba:      { label: 'Jobijoba',       bg: '#ecfdf5', color: '#065f46' },
  figaro:        { label: 'Figaro Emploi',  bg: '#f8fafc', color: '#475569' },
  jooble:        { label: 'Jooble',         bg: '#fff1f2', color: '#be123c' },
};

/* ─── Types ──────────────────────────────────────────────────────────────── */

interface Offre {
  id: number;
  title: string;
  company: string;
  location: string;
  url: string;
  source: string;
  type_contrat_detail: string;
  is_new: boolean;
  display_order: number;
}

/* ─── Page principale ────────────────────────────────────────────────────── */

const AlertesClients: React.FC = () => {
  const [offresBySlug, setOffresBySlug] = useState<Record<string, Offre[]>>({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedSlugs, setSelectedSlugs] = useState<Set<string>>(new Set());
  const [expandedSlugs, setExpandedSlugs] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ sent: number; skipped: number } | null>(null);
  const [sendError, setSendError] = useState('');
  // Page admin : on affiche toujours tous les slugs
  const subscribedSlugs = SLUG_ORDER; // pas de filtre abonnement côté admin

  // ── Drag & drop reorder ──────────────────────────────────────────────────
  const dragRef = useRef<{ slug: string; idx: number } | null>(null);
  const [dragOver, setDragOver] = useState<{ slug: string; idx: number; pos: 'top' | 'bottom' } | null>(null);

  const handleDragStart = useCallback((slug: string, idx: number) => {
    dragRef.current = { slug, idx };
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, slug: string, idx: number) => {
    e.preventDefault();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const pos = e.clientY < rect.top + rect.height / 2 ? 'top' : 'bottom';
    setDragOver(prev =>
      prev?.slug === slug && prev.idx === idx && prev.pos === pos ? prev : { slug, idx, pos }
    );
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    // Ne pas effacer si on entre dans un enfant
    if ((e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) return;
    setDragOver(null);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent, slug: string, toIdx: number) => {
    e.preventDefault();
    const drag = dragRef.current;
    setDragOver(null);
    dragRef.current = null;
    if (!drag || drag.slug !== slug || drag.idx === toIdx) return;

    const offres = [...(offresBySlug[slug] || [])];
    const [moved] = offres.splice(drag.idx, 1);

    // Calcul position d'insertion après retrait de l'élément source
    let insertAt = dragOver?.pos === 'bottom' ? toIdx + 1 : toIdx;
    if (drag.idx < insertAt) insertAt--;
    offres.splice(Math.max(0, Math.min(insertAt, offres.length)), 0, moved);

    // Mise à jour locale optimiste
    setOffresBySlug(prev => ({ ...prev, [slug]: offres }));

    // Persistance DB
    try {
      await fetch(`${API}/newsletters/offres-reorder/`, {
        method: 'PATCH',
        headers: await apiHeaders(),
        body: JSON.stringify({ slug, ids: offres.map(o => o.id) }),
      });
    } catch {
      // Silencieux — l'ordre local reste en place
    }
  }, [offresBySlug, dragOver]);

  const handleDragEnd = useCallback(() => {
    setDragOver(null);
    dragRef.current = null;
  }, []);

  const fetchOffres = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const offresRes = await fetch(`${API}/newsletters/offres-du-jour/`, { headers: await apiHeaders() });

      if (!offresRes.ok) return;
      const data: Record<string, Offre[]> = await offresRes.json();
      setOffresBySlug(data);

      // Auto-expand et auto-sélectionner les slugs qui ont des offres
      const withOffers = new Set(
        SLUG_ORDER.filter(slug => (data[slug] || []).length > 0)
      );
      setExpandedSlugs(withOffers);
      setSelectedSlugs(withOffers);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchOffres(); }, [fetchOffres]);

  const handleRefresh = () => {
    setSendResult(null);
    setSendError('');
    fetchOffres(true);
  };

  const toggleSlugSelected = (slug: string) => {
    setSelectedSlugs(prev => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const toggleSlugExpanded = (slug: string) => {
    setExpandedSlugs(prev => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const handleSend = async () => {
    setSending(true);
    setSendResult(null);
    setSendError('');
    try {
      const res = await fetch(`${API}/newsletters/send/`, {
        method: 'POST',
        headers: await apiHeaders(),
        body: JSON.stringify({ slugs: Array.from(selectedSlugs) }),
      });
      const data = await res.json();
      if (res.ok) setSendResult(data);
      else setSendError(data.error || 'Erreur inconnue');
    } catch {
      setSendError('Erreur réseau');
    } finally {
      setSending(false);
    }
  };

  // Slugs à afficher : seulement ceux auxquels l'user est abonné, dans l'ordre SLUG_ORDER
  const displaySlugs = SLUG_ORDER; // page admin : tous les slugs toujours visibles

  const totalOffres = displaySlugs.reduce(
    (acc, slug) => acc + (offresBySlug[slug] || []).length,
    0
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 rounded-full border-2 animate-spin" style={{ borderColor: '#2a52a8', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  return (
    <div className="p-8 w-full">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Newsletters du jour</h1>
          <p className="text-sm text-gray-500 mt-1">
            {totalOffres} offre{totalOffres !== 1 ? 's' : ''} importée{totalOffres !== 1 ? 's' : ''} aujourd'hui
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all disabled:opacity-50"
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            Rafraîchir
          </button>
          {selectedSlugs.size > 0 && (
            <button
              onClick={handleSend}
              disabled={sending}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-all shadow-sm hover:opacity-90 disabled:opacity-50"
              style={{ background: '#2a52a8' }}
            >
              {sending
                ? <><RefreshCw size={14} className="animate-spin" /> Envoi…</>
                : <><Mail size={14} /> Envoyer ({selectedSlugs.size} sélectionnée{selectedSlugs.size > 1 ? 's' : ''})</>
              }
            </button>
          )}
        </div>
      </div>

      {/* Bandeau résultat envoi */}
      {sendResult && (
        <div className="mb-5 px-4 py-3 rounded-xl text-sm font-medium bg-green-50 text-green-700 border border-green-200">
          ✅ {sendResult.sent} mail{sendResult.sent !== 1 ? 's' : ''} envoyé{sendResult.sent !== 1 ? 's' : ''}, {sendResult.skipped} ignoré{sendResult.skipped !== 1 ? 's' : ''}
        </div>
      )}
      {sendError && (
        <div className="mb-5 px-4 py-3 rounded-xl text-sm font-medium bg-red-50 text-red-700 border border-red-200">
          ❌ {sendError}
        </div>
      )}

      {/* Liste des slugs */}
      <div className="flex flex-col gap-4">
        {displaySlugs.map(slug => {
          const offres = offresBySlug[slug] || [];
          const count = offres.length;
          const color = SLUG_COLOR[slug] || '#2a52a8';
          const label = SLUG_LABEL[slug] || slug;
          const isSelected = selectedSlugs.has(slug);
          const isExpanded = expandedSlugs.has(slug);
          const hasOffers = count > 0;

          return (
            <div key={slug} className="flex items-start gap-3">
              {/* Checkbox */}
              <div className="mt-3 flex-shrink-0">
                <input
                  type="checkbox"
                  checked={isSelected}
                  disabled={!hasOffers}
                  onChange={() => toggleSlugSelected(slug)}
                  className="w-4 h-4 rounded cursor-pointer accent-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                />
              </div>

              {/* Card */}
              <div className="flex-1 rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
                {/* Header coloré */}
                <div
                  className="flex items-center justify-between px-5 py-3.5 cursor-pointer select-none"
                  style={{ background: color }}
                  onClick={() => hasOffers && toggleSlugExpanded(slug)}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-white font-bold text-sm">{label}</span>
                    {hasOffers ? (
                      <span
                        className="text-xs font-semibold px-2 py-0.5 rounded-full"
                        style={{ background: 'rgba(255,255,255,0.2)', color: 'white' }}
                      >
                        {count} offre{count !== 1 ? 's' : ''}
                      </span>
                    ) : (
                      <span className="text-xs text-white/60 italic">Aucune offre importée aujourd'hui</span>
                    )}
                  </div>
                  {hasOffers && (
                    <button
                      className="text-white/80 hover:text-white transition-colors"
                      onClick={e => { e.stopPropagation(); toggleSlugExpanded(slug); }}
                    >
                      {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                  )}
                </div>

                {/* Corps — liste des offres */}
                {isExpanded && hasOffers && (
                  <div className="bg-white divide-y divide-gray-50">
                    {offres.map((offre, idx) => {
                      const badge = SOURCE_BADGE[offre.source];
                      const meta = [offre.company, offre.location, offre.type_contrat_detail]
                        .filter(Boolean)
                        .join(' · ');
                      const isOverTop    = dragOver?.slug === slug && dragOver.idx === idx && dragOver.pos === 'top';
                      const isOverBottom = dragOver?.slug === slug && dragOver.idx === idx && dragOver.pos === 'bottom';

                      return (
                        <div
                          key={offre.id ?? idx}
                          draggable
                          onDragStart={() => handleDragStart(slug, idx)}
                          onDragOver={(e) => handleDragOver(e, slug, idx)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, slug, idx)}
                          onDragEnd={handleDragEnd}
                          className={[
                            'flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50 transition-colors group cursor-default select-none',
                            isOverTop    ? 'border-t-2 border-blue-500' : '',
                            isOverBottom ? 'border-b-2 border-blue-500' : '',
                          ].join(' ')}
                        >
                          {/* Drag handle */}
                          <div className="flex-shrink-0 text-gray-300 hover:text-blue-400 cursor-grab active:cursor-grabbing transition-colors" title="Réordonner">
                            <GripVertical size={14} />
                          </div>

                          {/* Source badge */}
                          <div className="flex-shrink-0">
                            {badge
                              ? <span className="text-xs font-semibold px-2 py-0.5 rounded-md" style={{ background: badge.bg, color: badge.color }}>{badge.label}</span>
                              : <span className="text-xs font-semibold px-2 py-0.5 rounded-md bg-gray-100 text-gray-600">{offre.source}</span>
                            }
                          </div>

                          {/* Titre + meta */}
                          <div className="flex-1 min-w-0">
                            <a
                              href={offre.url || '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-semibold text-gray-900 group-hover:text-blue-700 transition-colors truncate block"
                            >
                              {offre.title}
                            </a>
                            {meta && (
                              <p className="text-xs text-gray-400 mt-0.5 truncate">{meta}</p>
                            )}
                          </div>

                          {/* Bouton Voir */}
                          <a
                            href={offre.url || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-shrink-0 p-1.5 rounded-lg text-gray-300 hover:text-blue-500 hover:bg-blue-50 transition-all"
                            title="Voir l'offre"
                            onClick={e => e.stopPropagation()}
                          >
                            <ExternalLink size={14} />
                          </a>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AlertesClients;
